/**
 * MeWallet — Prepaid wallet home
 *   /me/wallet
 *   • Big balance card + eligibility hint (need ₹1000 to book)
 *   • Recharge via Razorpay (min ₹500)
 *   • Transaction history
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { custApi, formatErr } from "../../lib/customerApi";
import {
  ArrowLeft, Wallet, Plus, ArrowUpRight, ArrowDownLeft,
  CheckCircle, Warning, Gift, CurrencyInr, Clock, ArrowClockwise,
} from "@phosphor-icons/react";

function loadScript(src) {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
    const s = document.createElement("script");
    s.src = src; s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const KIND_META = {
  recharge_gateway: { label: "Recharge",         color: "#15803D", bg: "#DCFCE7", Icon: ArrowDownLeft, sign: "+" },
  recharge_staff:   { label: "Staff credit",     color: "#15803D", bg: "#DCFCE7", Icon: ArrowDownLeft, sign: "+" },
  referral:         { label: "Referral bonus",   color: "#7C3AED", bg: "#EDE9FE", Icon: Gift,          sign: "+" },
  debit_service:    { label: "Service payment",  color: "#DC2626", bg: "#FEE2E2", Icon: ArrowUpRight,  sign: "-" },
  refund:           { label: "Refund",           color: "#0891B2", bg: "#CFFAFE", Icon: ArrowDownLeft, sign: "+" },
  refund_cancel:    { label: "Refund on cancel", color: "#EA580C", bg: "#FFEDD5", Icon: ArrowUpRight,  sign: "-" },
  adjustment:       { label: "Adjustment",       color: "#5C5A55", bg: "#F3F4F6", Icon: CheckCircle,   sign: "" },
};

export default function MeWallet() {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState("1000");
  const [busy, setBusy] = useState(false);
  // Iter 220e — auto-refresh polling. Whenever a pending recharge is
  // visible we lightly poll `/me/wallet` so the credit lands in the UI
  // the moment the Razorpay webhook (or the 15-min reconcile cron)
  // finalises it — no manual refresh needed. `pollingReason` is only
  // rendered when the user just clicked Pay, so we get a nice "Waiting
  // for confirmation…" banner without spooking existing customers who
  // opened the page cold.
  const [pollingReason, setPollingReason] = useState(null);   // "checkout" | null
  const pollTimer = useRef(null);
  const pollDeadline = useRef(0);

  const load = async () => {
    try {
      const r = await custApi.get("/me/wallet");
      setData(r.data);
      return r.data;
    } catch (e) { toast.error(formatErr(e)); }
    return null;
  };
  useEffect(() => { load(); }, []);

  // Iter 220e — as long as any pending recharge_gateway is visible, poll
  // every 6 s so the user sees the credit land automatically. We keep
  // polling for up to 3 minutes after checkout was opened, or as long as
  // pending rows are visible (with a 6-min ceiling from mount) so a cold
  // page doesn't hammer the API forever.
  useEffect(() => {
    const hasPending = (data?.recent_txns || []).some(
      (t) => t.kind === "recharge_gateway" && t.status === "pending",
    );
    // Clear any active timer if there's nothing to wait for.
    if (!hasPending && pollingReason !== "checkout") {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
      return;
    }
    if (pollTimer.current) return;   // already running
    // Set / extend the deadline
    if (!pollDeadline.current) {
      pollDeadline.current = Date.now() + (pollingReason === "checkout" ? 180_000 : 360_000);
    }
    pollTimer.current = setInterval(async () => {
      if (Date.now() > pollDeadline.current) {
        clearInterval(pollTimer.current); pollTimer.current = null; pollDeadline.current = 0;
        return;
      }
      const before = data?.balance ?? 0;
      const fresh = await load();
      if (!fresh) return;
      const stillPending = (fresh.recent_txns || []).some(
        (t) => t.kind === "recharge_gateway" && t.status === "pending",
      );
      if (fresh.balance > before) {
        toast.success(`₹${fresh.balance - before} credited to your wallet`);
      }
      if (!stillPending) {
        // Nothing pending anymore — stop polling.
        clearInterval(pollTimer.current); pollTimer.current = null; pollDeadline.current = 0;
        setPollingReason(null);
      }
    }, 6000);
    return () => {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.recent_txns, pollingReason]);

  const recharge = async () => {
    const amt = Number(amount);
    if (!amt || amt < 500) return toast.error("Minimum recharge is ₹500");
    setBusy(true);
    try {
      const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (!ok) throw new Error("Failed to load Razorpay");
      const order = await custApi.post("/me/wallet/recharge/create-order", { amount: amt });
      const rzp = new window.Razorpay({
        key: order.data.key_id,
        order_id: order.data.order_id,
        amount: order.data.amount,
        currency: order.data.currency,
        name: "Rescigo Wallet",
        description: `Recharge ₹${amt}`,
        prefill: order.data.customer,
        theme: { color: "#DC2626" },
        handler: async (resp) => {
          try {
            const v = await custApi.post("/me/wallet/recharge/verify", resp);
            toast.success(`₹${v.data.credited} credited to your wallet`);
            setPollingReason(null);
            load();
          } catch (e) {
            // Iter 220e — verify can fail on dodgy networks / UPI intents.
            // Fall through to the webhook + reconcile flow: keep polling
            // and reassure the user the credit is on its way.
            toast.message("Payment received — confirming with the bank…", {
              description: "Your wallet will update automatically.",
              duration: 5000,
            });
          }
        },
        modal: {
          ondismiss: () => setBusy(false),
        },
      });
      // Arm the auto-refresh WINDOW before opening the modal so a
      // successful UPI-intent capture (where the modal never returns)
      // still finalises via polling.
      setPollingReason("checkout");
      pollDeadline.current = Date.now() + 180_000;
      load();
      rzp.open();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const bal = data?.balance ?? 0;
  const eligible = bal >= (data?.min_booking_balance ?? 1000);
  const quickAmounts = [500, 1000, 2000, 5000];

  return (
    <div className="space-y-4 px-4 py-4 pb-8" data-testid="me-wallet">
      <div className="flex items-start gap-3">
        <Link to="/me" className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center hover:bg-gray-50">
          <ArrowLeft size={16} weight="bold"/>
        </Link>
        <div className="flex-1">
          <h1 className="font-display font-black text-[22px] tracking-tight text-[#111827]">My Wallet</h1>
          <div className="text-[12px] text-gray-500 mt-0.5">Prepaid balance · refundable on cancel</div>
        </div>
      </div>

      {/* Balance hero */}
      <div className="rounded-3xl p-5 bg-gradient-to-br from-[#DC2626] via-[#B91C1C] to-[#7F1D1D] text-white shadow-[0_20px_50px_rgba(220,38,38,0.25)]"
           data-testid="wallet-hero">
        <div className="flex items-center gap-2 opacity-90">
          <Wallet size={16} weight="fill"/>
          <div className="text-[10px] uppercase tracking-widest font-bold">Available balance</div>
        </div>
        <div className="font-display font-black text-4xl mt-1" data-testid="wallet-balance">
          ₹{bal.toLocaleString("en-IN")}
        </div>
        <div className="text-[11px] mt-2 opacity-95 inline-flex items-center gap-1.5">
          {eligible ? (
            <><CheckCircle size={13} weight="fill"/> Ready to book</>
          ) : (
            <><Warning size={13} weight="fill"/> Recharge to ₹{data?.min_booking_balance || 1000} to book services</>
          )}
        </div>
      </div>

      {/* Recharge card */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
        <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Recharge</div>
        <div className="text-[12px] text-gray-600 mt-0.5">Minimum ₹{data?.min_recharge || 500}</div>
        <div className="grid grid-cols-4 gap-1.5 mt-3" data-testid="wallet-quick-amounts">
          {quickAmounts.map(a => (
            <button key={a} onClick={() => setAmount(String(a))}
                    data-testid={`wallet-q-${a}`}
                    className={`h-11 rounded-md text-sm font-semibold border ${
                      String(a) === amount ? "bg-[#DC2626] text-white border-[#DC2626]"
                                            : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                    }`}>₹{a}</button>
          ))}
        </div>
        <div className="mt-2 flex gap-2">
          <div className="flex-1 relative">
            <CurrencyInr size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input type="number" value={amount}
                   onChange={e => setAmount(e.target.value)}
                   data-testid="wallet-amount"
                   placeholder="Enter amount"
                   className="w-full h-11 pl-8 pr-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>
          <button onClick={recharge} disabled={busy}
                  data-testid="wallet-pay"
                  className="h-11 px-5 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Plus size={13} weight="bold"/> {busy ? "…" : "Pay"}
          </button>
        </div>
      </div>

      {/* Recent txns */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
            Transactions
          </div>
          <button onClick={() => load()} data-testid="wallet-refresh"
                  className="text-[10px] uppercase tracking-widest text-[#DC2626] font-bold inline-flex items-center gap-1">
            <ArrowClockwise size={11} weight="bold" className={pollTimer.current ? "animate-spin" : ""}/>
            Refresh
          </button>
        </div>
        {/* Iter 220e — pending recharge banner: reassures the customer
            that their wallet will update automatically, no need to
            re-pay. Only shown while a pending gateway row is visible. */}
        {(data?.recent_txns || []).some((t) => t.kind === "recharge_gateway" && t.status === "pending") && (
          <div className="mb-2 rounded-md border border-[#F9E4B7] bg-[#FEF7E6] px-3 py-2 flex items-center gap-2"
               data-testid="wallet-pending-banner">
            <ArrowClockwise size={13} weight="bold" className="text-[#B58412] animate-spin shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-bold text-[#7A5A11]">Confirming your payment…</div>
              <div className="text-[10px] text-[#7A5A11]">Your wallet will update as soon as the bank confirms — usually under a minute.</div>
            </div>
          </div>
        )}
        {!data ? (
          <div className="text-xs text-gray-500 text-center py-4">Loading…</div>
        ) : data.recent_txns.length === 0 ? (
          <div className="border border-dashed border-[#E5E2D9] rounded-md p-6 text-center">
            <Wallet size={22} color="#8A8780" className="mx-auto mb-1"/>
            <div className="text-xs text-[#5C5A55]">No transactions yet.</div>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="wallet-txns">
            {data.recent_txns.map(t => {
              const m = KIND_META[t.kind] || KIND_META.adjustment;
              const pending = t.status === "pending" || t.status === "pending_verification";
              return (
                <div key={t.id} data-testid={`wallet-tx-${t.id}`}
                     className="flex items-center gap-3 bg-white border border-gray-100 rounded-md p-3">
                  <div className="w-9 h-9 rounded-full inline-flex items-center justify-center"
                       style={{ backgroundColor: m.bg, color: m.color }}>
                    <m.Icon size={14} weight="fill"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[#1A1A1A]">{m.label}</div>
                    <div className="text-[10px] text-gray-500">
                      {new Date(t.created_at).toLocaleDateString(undefined,{day:"numeric",month:"short",year:"numeric"})}
                      {pending && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#B45309] font-bold uppercase"><Clock size={8} className="inline mr-0.5"/> Pending</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-[14px] font-black ${t.amount >= 0 ? "text-[#15803D]" : "text-[#DC2626]"}`}>
                      {t.amount >= 0 ? "+" : ""}₹{Math.abs(t.amount).toLocaleString("en-IN")}
                    </div>
                    {t.balance_after != null && (
                      <div className="text-[9px] text-gray-500">Bal ₹{t.balance_after.toLocaleString("en-IN")}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
